import { Box, Grid, Modal } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Close } from '@HomaHealth/ui-component-library';
import { Button } from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import React from 'react';
import { scrollBarStyles } from '../../../core-utils/messages/CommonConstants';

export interface BasicModalProps {
  open: boolean;
  handleClose: () => any;

  title: string;
  isCloseBtn?: boolean;
  isSaveButton: boolean;
  saveBtnText?: string;
  saveBtnAction?: () => void;
  saveBtnDisabled?: boolean;
  width?: string;
  height?: string;
  isScrollable?: boolean;
  children?: React.ReactNode;
  saveBtnStartIcon?: React.ReactNode;
  closeBtnText?: string;
  max_height?: string;
}

const useStyles = makeStyles<{
  width?: string;
  height?: string;
  max_height?: string;
  isScrollable?: boolean;
}>()((theme, props) => ({
  root: {
    width: props.width ?? '720px',
    height: props.height ?? 'fit-content',
    maxHeight: props.height || props.max_height || '518px',
    backgroundColor: patientAccessTheme.palette.status.white,
    borderRadius: 8,
    padding: '1rem 1.5rem',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    flexDirection: 'column',
    outline: 'none',
  },
  separator: {
    width: '100%',
    height: 1,
    backgroundColor: patientAccessTheme.palette.grey[300],
    border: 'none',
  },
  content: {
    paddingRight: props.isScrollable ? '0.5rem' : '0',
    overflowY: 'auto',
    overflowX: 'hidden',
    maxHeight: props.height ?? '370px',
    ...scrollBarStyles,
  },
}));

const BasicModal: React.FC<BasicModalProps> = ({
  open,
  handleClose,
  title,
  isSaveButton,
  saveBtnText,
  saveBtnAction,
  isCloseBtn = true,
  saveBtnDisabled,
  width,
  height,
  isScrollable,
  children,
  saveBtnStartIcon,
  closeBtnText,
  max_height,
}) => {
  const { classes } = useStyles({ width, height, max_height, isScrollable });

  return (
    <Modal
      style={{ outline: 'none', border: 'none' }}
      open={open}
      onClose={handleClose}
      disableEnforceFocus
      disableAutoFocus
    >
      <Box
        className={classes.root}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
        }}
      >
        <Grid
          container
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography
              fontSize="18px"
              lineHeight="26px"
              fontType="medium"
              textColor={patientAccessTheme.palette.text.highemphasis}
            >
              {title}
            </Typography>
          </Box>
          <Box
            sx={{
              width: 14,
              height: 14,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Close
              height="14px"
              width="14px"
              fill={patientAccessTheme.palette.status.icon}
              cursor="pointer"
              onClick={handleClose}
            />
          </Box>
          <hr className={classes.separator} />
        </Grid>
        <Grid
          container
          style={{ gap: '1rem' }}
          sx={{
            flexDirection: 'column',
          }}
        >
          <Grid className={isScrollable ? classes.content : ''}>
            {children}
          </Grid>
        </Grid>
        <Grid
          sx={{
            display: 'flex',
            gap: '1rem',
            marginLeft: 'auto',
          }}
        >
          {isCloseBtn && (
            <Button
              color="primary"
              height="36px"
              width="150px"
              label={closeBtnText || 'Close'}
              onClick={handleClose}
            />
          )}

          {isSaveButton && (
            <Button
              color="primary"
              variant="contained"
              height="36px"
              width="fit-content"
              label={saveBtnText || 'Save'}
              onClick={saveBtnAction}
              disabled={saveBtnDisabled}
              startIcon={saveBtnStartIcon ?? <></>}
              styleProps={{
                minWidth: '150px',
              }}
            />
          )}
        </Grid>
      </Box>
    </Modal>
  );
};

export default BasicModal;
